import { useState, useEffect, useMemo } from "react";
import AppLayout from "@/components/layout/AppLayout";
import { apiPrivate } from "@/lib/api";
import { Send, Eye, Users, CheckCircle2, AlertTriangle, Loader2, Mail, History, X } from "lucide-react";
import { toast } from "sonner";

const QUOTA_OPTIONS = ["A", "B", "C", "D"];

export default function AdminCommunications() {
    const [tab, setTab] = useState("compose"); // compose | history
    const [subject, setSubject] = useState("");
    const [bodyHtml, setBodyHtml] = useState("");
    const [segmentType, setSegmentType] = useState("all");
    const [quotaCats, setQuotaCats] = useState([]);
    const [preview, setPreview] = useState(null);
    const [previewing, setPreviewing] = useState(false);
    const [sending, setSending] = useState(false);
    const [confirm, setConfirm] = useState(false);
    const [history, setHistory] = useState([]);

    const loadHistory = () => apiPrivate.get("/admin/communications").then(r => setHistory(r.data)).catch(() => {});
    useEffect(() => { if (tab === "history") loadHistory(); }, [tab]);

    const toggleQuota = (q) => setQuotaCats(c => c.includes(q) ? c.filter(x => x !== q) : [...c, q]);

    const segmentValid = useMemo(() => {
        if (segmentType === "all") return true;
        if (segmentType === "quota") return quotaCats.length > 0;
        return false; // manual not supported in this MVP UI (only via API)
    }, [segmentType, quotaCats]);

    const doPreview = async () => {
        setPreviewing(true);
        try {
            const r = await apiPrivate.post("/admin/communications/preview", {
                segment_type: segmentType, quota_categories: quotaCats, only_active: true,
            });
            setPreview(r.data);
        } catch (e) {
            toast.error("Errore nell'anteprima destinatari");
        }
        setPreviewing(false);
    };

    const doSend = async () => {
        setSending(true);
        try {
            const r = await apiPrivate.post("/admin/communications/send", {
                subject, body_html: bodyHtml, segment_type: segmentType,
                quota_categories: quotaCats, only_active: true,
            });
            toast.success(`Comunicazione inviata a ${r.data.sent_count} destinatari`);
            setSubject(""); setBodyHtml(""); setPreview(null); setConfirm(false);
            setTab("history");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Errore nell'invio");
        }
        setSending(false);
    };

    return (
        <AppLayout admin>
            <div className="max-w-[1400px] mx-auto" data-testid="admin-communications">
                <div>
                    <span className="label-tag text-magenta">Comunicazioni</span>
                    <h1 className="font-display text-5xl mt-3 tracking-tighter">
                        Invia <span className="font-serif-italic text-gradient-tropical">a chi conta</span>.
                    </h1>
                    <p className="text-zinc-400 mt-3 max-w-2xl">
                        Componi e invia email mirate ai soci selezionati per categoria di quota. Anteprima destinatari prima dell'invio.
                    </p>
                </div>

                {/* Tabs */}
                <div className="mt-8 flex items-center gap-2 border-b border-white/10">
                    <TabBtn active={tab === "compose"} onClick={() => setTab("compose")} testid="tab-compose" icon={Send}>Nuovo invio</TabBtn>
                    <TabBtn active={tab === "history"} onClick={() => setTab("history")} testid="tab-history" icon={History}>Storico</TabBtn>
                </div>

                {tab === "compose" ? (
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-8">
                        {/* Composer */}
                        <div className="lg:col-span-2 bento p-6 space-y-5">
                            <div>
                                <div className="label-tag mb-2">Oggetto</div>
                                <input
                                    value={subject}
                                    onChange={(e) => setSubject(e.target.value)}
                                    placeholder="Es. Aggiornamento Q2 2026"
                                    data-testid="comm-subject"
                                    className="w-full bg-[#0F0F0F] border border-white/10 text-white px-3 py-2.5 font-grotesk focus:outline-none focus:border-bayside rounded-2xl"
                                />
                            </div>
                            <div>
                                <div className="label-tag mb-2">Corpo email (supporto HTML)</div>
                                <textarea
                                    value={bodyHtml}
                                    onChange={(e) => setBodyHtml(e.target.value)}
                                    placeholder="Buongiorno,\n\nVi scrivo per condividere..."
                                    rows={12}
                                    data-testid="comm-body"
                                    className="w-full bg-[#0F0F0F] border border-white/10 text-white p-3 font-grotesk focus:outline-none focus:border-bayside resize-y rounded-2xl text-sm"
                                />
                                <div className="text-xs text-zinc-500 mt-2 font-grotesk">
                                    Tip: puoi usare HTML semplice come {"<p>"}, {"<strong>"}, {"<a href=…>"}, {"<br/>"}.
                                </div>
                            </div>
                        </div>

                        {/* Segmento */}
                        <div className="space-y-5">
                            <div className="bento p-5">
                                <div className="label-tag mb-3">Segmento destinatari</div>
                                <div className="space-y-2">
                                    <SegmentRadio active={segmentType === "all"} label="Tutti i soci attivi" onClick={() => setSegmentType("all")} testid="seg-all" />
                                    <SegmentRadio active={segmentType === "quota"} label="Per categoria quota" onClick={() => setSegmentType("quota")} testid="seg-quota" />
                                </div>
                                {segmentType === "quota" && (
                                    <div className="mt-4 flex flex-wrap gap-2" data-testid="quota-picker">
                                        {QUOTA_OPTIONS.map(q => (
                                            <button
                                                key={q}
                                                onClick={() => toggleQuota(q)}
                                                data-testid={`quota-${q}`}
                                                className={`text-xs uppercase tracking-widest font-grotesk px-3 py-2 rounded-full border transition ${
                                                    quotaCats.includes(q) ? "border-bayside bg-bayside/15 text-white" : "border-white/10 text-zinc-400 hover:text-white"
                                                }`}
                                            >Quota {q}</button>
                                        ))}
                                    </div>
                                )}
                            </div>

                            <div className="bento p-5">
                                <button
                                    onClick={doPreview}
                                    disabled={previewing || !segmentValid}
                                    data-testid="comm-preview-btn"
                                    className="btn-ghost w-full justify-center"
                                >
                                    {previewing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />}
                                    Anteprima destinatari
                                </button>
                                {preview && (
                                    <div className="mt-4" data-testid="comm-preview-result">
                                        <div className="flex items-center justify-between mb-3">
                                            <div className="flex items-center gap-2 text-bayside">
                                                <Users className="h-4 w-4" />
                                                <span className="font-display text-2xl">{preview.count}</span>
                                            </div>
                                            <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-grotesk">destinatari</span>
                                        </div>
                                        <div className="max-h-44 overflow-y-auto border border-white/5 rounded-xl divide-y divide-white/5" data-testid="comm-recipients">
                                            {preview.recipients.slice(0, 10).map(r => (
                                                <div key={r.id} className="px-3 py-2 text-xs font-grotesk">
                                                    <div className="text-white truncate">{r.display_name}</div>
                                                    <div className="text-zinc-500 truncate">{r.email}</div>
                                                </div>
                                            ))}
                                            {preview.count > 10 && (
                                                <div className="px-3 py-2 text-[10px] text-zinc-500 font-grotesk uppercase tracking-widest text-center">
                                                    + altri {preview.count - 10} destinatari
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                                <button
                                    onClick={() => setConfirm(true)}
                                    disabled={!subject || !bodyHtml || !preview || preview.count === 0}
                                    data-testid="comm-send-btn"
                                    className="btn-rebel w-full justify-center mt-4"
                                >
                                    <Send className="h-4 w-4" /> Invia comunicazione
                                </button>
                            </div>
                        </div>
                    </div>
                ) : (
                    <HistoryTable history={history} />
                )}

                {confirm && (
                    <ConfirmModal
                        count={preview?.count || 0}
                        subject={subject}
                        sending={sending}
                        onCancel={() => setConfirm(false)}
                        onConfirm={doSend}
                    />
                )}
            </div>
        </AppLayout>
    );
}

function TabBtn({ active, onClick, children, testid, icon: Icon }) {
    return (
        <button
            onClick={onClick}
            data-testid={testid}
            className={`inline-flex items-center gap-2 px-5 py-3 text-sm font-grotesk uppercase tracking-widest transition ${
                active ? "text-white border-b-2 border-bayside" : "text-zinc-500 hover:text-white"
            }`}
        >
            <Icon className="h-4 w-4" /> {children}
        </button>
    );
}

function SegmentRadio({ active, label, onClick, testid }) {
    return (
        <button
            onClick={onClick}
            data-testid={testid}
            className={`w-full text-left px-3 py-2.5 rounded-xl border transition flex items-center gap-3 ${
                active ? "border-bayside bg-bayside/10 text-white" : "border-white/10 text-zinc-400 hover:text-white"
            }`}
        >
            <span className={`inline-block h-3 w-3 rounded-full border-2 ${active ? "bg-bayside border-bayside" : "border-white/40"}`}></span>
            <span className="text-sm font-grotesk">{label}</span>
        </button>
    );
}

function HistoryTable({ history }) {
    if (history.length === 0) {
        return (
            <div className="bento p-12 text-center text-zinc-500 mt-8" data-testid="comm-history-empty">
                <Mail className="h-10 w-10 mx-auto mb-3 opacity-40" />
                Nessuna comunicazione inviata.
            </div>
        );
    }
    return (
        <div className="space-y-3 mt-8" data-testid="comm-history-list">
            {history.map(h => (
                <div key={h.id} className="bento p-5" data-testid={`comm-history-${h.id}`}>
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                        <div className="min-w-0">
                            <h3 className="font-display text-lg leading-tight">{h.subject}</h3>
                            <p className="text-xs text-zinc-500 font-grotesk mt-1">
                                {h.sent_by} · {new Date(h.sent_at).toLocaleString("it-IT")}
                            </p>
                        </div>
                        <div className="flex items-center gap-4">
                            <div className="text-right">
                                <div className="font-display text-xl text-emerald-300">{h.sent_count}</div>
                                <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-grotesk">inviati</div>
                            </div>
                            {h.failed_count > 0 && (
                                <div className="text-right">
                                    <div className="font-display text-xl text-vermillion">{h.failed_count}</div>
                                    <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-grotesk">falliti</div>
                                </div>
                            )}
                            <span className="text-xs font-grotesk px-3 py-1 border border-white/10 rounded-full text-zinc-300">
                                {h.segment_type === "all" ? "Tutti" : h.segment_type === "quota" ? `Quote ${h.quota_categories?.join(",")}` : "Manuale"}
                            </span>
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
}

function ConfirmModal({ count, subject, sending, onCancel, onConfirm }) {
    return (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={!sending ? onCancel : undefined} data-testid="comm-confirm-modal">
            <div className="bg-[#0a0c1c] border border-white/10 rounded-2xl max-w-md w-full p-7" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-start gap-3 mb-4">
                    <AlertTriangle className="h-6 w-6 text-vermillion shrink-0 mt-1" />
                    <div>
                        <h3 className="font-display text-2xl tracking-tight">Confermi l'invio?</h3>
                        <p className="text-zinc-400 text-sm mt-2">
                            Stai per inviare <strong className="text-white">"{subject}"</strong> a <strong className="text-bayside">{count} destinatari</strong>. L'operazione è irreversibile.
                        </p>
                    </div>
                </div>
                <div className="flex justify-end gap-3 mt-6">
                    <button onClick={onCancel} disabled={sending} className="btn-ghost" data-testid="confirm-cancel">Annulla</button>
                    <button onClick={onConfirm} disabled={sending} className="btn-rebel" data-testid="confirm-send">
                        {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <><CheckCircle2 className="h-4 w-4" />Conferma invio</>}
                    </button>
                </div>
            </div>
        </div>
    );
}
